// 重复类型计算逻辑
import type { RecurrenceConfig, RecurrenceType } from '@ext-bookmark/shared/types';

/**
 * 计算指定日期范围内所有符合条件的日期
 * @param config 重复配置
 * @param startDate 重复开始日期
 * @param dateRange 查询的日期范围
 * @param todoEndDate 待办事项的结束日期（可选，如果提供，会限制重复的结束日期）
 */
export function calculateRecurrenceDates(
  config: RecurrenceConfig,
  startDate: Date,
  dateRange: { start: Date; end: Date },
  todoEndDate?: Date
): Date[] {
  const dates: Date[] = [];
  const current = new Date(startDate);
  const rangeStart = new Date(dateRange.start);
  const rangeEnd = new Date(dateRange.end);
  
  // 如果待办事项有结束日期，使用较小的结束日期
  const effectiveEnd = todoEndDate && todoEndDate < rangeEnd ? todoEndDate : rangeEnd;
  
  // 准备例外日期集合（用于快速查找）
  const exceptionDatesSet = new Set<number>();
  if (config.exceptionDates && config.exceptionDates.length > 0) {
    config.exceptionDates.forEach(timestamp => {
      const date = new Date(timestamp);
      date.setHours(0, 0, 0, 0);
      exceptionDatesSet.add(date.getTime());
    });
  }
  
  // 限制最大计算数量，避免无限循环
  const maxIterations = 10000;
  let iterations = 0;
  let occurrenceCount = 0; // 已生成的重复次数计数
  const maxOccurrences = config.maxOccurrences; // 最大重复次数限制

  // 当 current 早于查询范围时，必须按重复规则步进到 >= rangeStart，不能直接设为 rangeStart，
  // 否则会漏掉落在范围内的重复日（例如每3天从 1/25 起：1/25,28,31,2/3... 若直接跳到 2/1 再 +3 会得到 2/4,7... 漏掉 2/3）
  let catchUpIterations = 0;
  while (current < rangeStart && catchUpIterations < maxIterations) {
    catchUpIterations++;
    moveToNextPossibleDate(current, config, startDate);
    if (current > effectiveEnd) return dates;
  }

  // 如果设置了最大重复次数，需要从开始日期开始计算所有符合条件的日期
  // 然后只返回在查询范围内的日期
  const allValidDates: Date[] = [];
  let tempCurrent = new Date(startDate);
  tempCurrent.setHours(0, 0, 0, 0);
  
  // 如果设置了最大重复次数，先计算所有符合条件的日期
  if (maxOccurrences) {
    while (occurrenceCount < maxOccurrences && iterations < maxIterations) {
      iterations++;
      
      if (isDateInRecurrence(tempCurrent, config, startDate)) {
        const tempTimestamp = new Date(tempCurrent);
        tempTimestamp.setHours(0, 0, 0, 0);
        if (!exceptionDatesSet.has(tempTimestamp.getTime())) {
          allValidDates.push(new Date(tempCurrent));
          occurrenceCount++;
        }
      }
      
      moveToNextPossibleDate(tempCurrent, config, startDate);
      
      // 如果超过了有效结束日期，停止计算
      if (todoEndDate && tempCurrent > todoEndDate) {
        break;
      }
    }
    
    // 从所有有效日期中筛选出在查询范围内的日期
    for (const date of allValidDates) {
      if (date >= rangeStart && date <= rangeEnd) {
        dates.push(date);
      }
    }
  } else {
    // 没有最大重复次数限制，使用原来的逻辑
    while (current <= effectiveEnd && iterations < maxIterations) {
      iterations++;
      
      // 检查日期是否在重复规则中
      if (isDateInRecurrence(current, config, startDate)) {
        // 检查是否在查询范围内
        if (current >= rangeStart && current <= rangeEnd) {
          // 检查是否在例外日期中
          const currentTimestamp = new Date(current);
          currentTimestamp.setHours(0, 0, 0, 0);
          if (!exceptionDatesSet.has(currentTimestamp.getTime())) {
            dates.push(new Date(current));
          }
        }
      }
      
      // 移动到下一个可能的日期
      moveToNextPossibleDate(current, config, startDate);
    }
  }
  
  return dates;
}

/**
 * 判断指定日期是否符合重复规则
 */
export function isDateInRecurrence(
  date: Date,
  config: RecurrenceConfig,
  startDate: Date
): boolean {
  const { type } = config;
  
  switch (type) {
    case 'daily':
      return isDailyRecurrence(date, startDate, config);
    
    case 'weekday':
      return isWeekdayRecurrence(date);
    
    case 'weekly':
      return isWeeklyRecurrence(date, startDate, config);
    
    case 'monthly':
      return isMonthlyRecurrence(date, startDate, config);
    
    case 'monthly-last-day':
      return isMonthlyLastDayRecurrence(date);
    
    case 'quarterly':
      return isQuarterlyRecurrence(date, startDate, config);
    
    case 'yearly':
      return isYearlyRecurrence(date, startDate, config);
    
    case 'lunar-yearly':
      return isLunarYearlyRecurrence(date, config);
    
    case 'legal-workday':
      return isLegalWorkdayRecurrence(date);
    
    case 'ebbinghaus':
      return isEbbinghausRecurrence(date, startDate, config);
    
    case 'custom-daily':
      return isCustomDailyRecurrence(date, startDate, config);
    
    case 'custom-weekly':
      return isCustomWeeklyRecurrence(date, startDate, config);
    
    case 'custom-monthly':
      return isCustomMonthlyRecurrence(date, startDate, config);
    
    case 'custom-yearly':
      return isCustomYearlyRecurrence(date, startDate, config);
    
    default:
      return false;
  }
}

/**
 * 移动到下一个可能的日期
 */
function moveToNextPossibleDate(
  current: Date,
  config: RecurrenceConfig,
  startDate: Date
): void {
  const { type } = config;
  
  switch (type) {
    case 'daily':
    case 'custom-daily':
      current.setDate(current.getDate() + (config.interval || 1));
      break;
    
    case 'weekday':
      // 移动到下一个工作日
      do {
        current.setDate(current.getDate() + 1);
      } while (current.getDay() === 0 || current.getDay() === 6);
      break;
    
    case 'weekly':
    case 'custom-weekly':
      current.setDate(current.getDate() + 7 * (config.interval || 1));
      break;
    
    case 'monthly':
    case 'custom-monthly':
      current.setMonth(current.getMonth() + (config.interval || 1));
      break;
    
    case 'monthly-last-day':
      current.setMonth(current.getMonth() + 1);
      break;
    
    case 'quarterly':
      current.setMonth(current.getMonth() + 3);
      break;
    
    case 'yearly':
    case 'custom-yearly':
      current.setFullYear(current.getFullYear() + (config.interval || 1));
      break;
    
    case 'lunar-yearly':
      // 农历年处理较复杂，这里简化处理
      current.setFullYear(current.getFullYear() + 1);
      break;
    
    case 'legal-workday':
      // 法定工作日处理较复杂，这里简化处理
      current.setDate(current.getDate() + 1);
      break;
    
    case 'ebbinghaus':
      // 艾宾浩斯需要特殊处理
      moveToNextEbbinghausDate(current, startDate, config);
      break;
    
    default:
      current.setDate(current.getDate() + 1);
  }
}

// ==================== 各种重复类型的判断函数 ====================

function isDailyRecurrence(date: Date, startDate: Date, config: RecurrenceConfig): boolean {
  const interval = config.interval || 1;
  const daysDiff = Math.floor((date.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
  return daysDiff >= 0 && daysDiff % interval === 0;
}

function isWeekdayRecurrence(date: Date): boolean {
  const day = date.getDay();
  return day >= 1 && day <= 5; // 周一到周五
}

function isWeeklyRecurrence(date: Date, startDate: Date, config: RecurrenceConfig): boolean {
  const weekday = config.weekday !== undefined ? config.weekday : startDate.getDay();
  return date.getDay() === weekday;
}

function isMonthlyRecurrence(date: Date, startDate: Date, config: RecurrenceConfig): boolean {
  const dayOfMonth = config.dayOfMonth !== undefined ? config.dayOfMonth : startDate.getDate();
  
  // 处理月份天数不同的问题（如2月只有28/29天）
  const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const targetDay = Math.min(dayOfMonth, lastDayOfMonth);
  
  return date.getDate() === targetDay;
}

function isMonthlyLastDayRecurrence(date: Date): boolean {
  const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  return date.getDate() === lastDayOfMonth;
}

function isQuarterlyRecurrence(date: Date, startDate: Date, config: RecurrenceConfig): boolean {
  const quarterDay = config.quarterDay || {
    month: startDate.getMonth(),
    day: startDate.getDate(),
  };
  
  // 检查是否在季度中的正确月份
  const quarter = Math.floor(date.getMonth() / 3);
  const targetQuarter = Math.floor(quarterDay.month / 3);
  
  if (quarter !== targetQuarter) {
    return false;
  }
  
  // 检查日期是否匹配
  const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const targetDay = Math.min(quarterDay.day, lastDayOfMonth);
  
  return date.getDate() === targetDay && date.getMonth() % 3 === quarterDay.month % 3;
}

function isYearlyRecurrence(date: Date, startDate: Date, config: RecurrenceConfig): boolean {
  const yearDay = config.yearDay || {
    month: startDate.getMonth(),
    day: startDate.getDate(),
  };
  
  return date.getMonth() === yearDay.month && date.getDate() === yearDay.day;
}

function isLunarYearlyRecurrence(_date: Date, config: RecurrenceConfig): boolean {
  // 农历计算较复杂，这里简化处理
  // 实际应用中需要使用农历转换库
  if (!config.lunarMonth || !config.lunarDay) {
    return false;
  }
  
  // TODO: 实现农历转换逻辑
  // 这里暂时返回 false，需要集成农历转换库
  return false;
}

function isLegalWorkdayRecurrence(date: Date): boolean {
  // 法定工作日需要排除法定节假日
  // 这里简化处理，只检查是否为工作日
  const day = date.getDay();
  if (day === 0 || day === 6) {
    return false;
  }
  
  // TODO: 实现法定节假日检查逻辑
  // 需要维护法定节假日列表
  return true;
}

function isEbbinghausRecurrence(date: Date, _startDate: Date, config: RecurrenceConfig): boolean {
  if (!config.ebbinghausStartDate || !config.ebbinghausStages) {
    return false;
  }
  
  const start = new Date(config.ebbinghausStartDate);
  const daysDiff = Math.floor((date.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  
  // 检查是否在复习阶段
  return config.ebbinghausStages.includes(daysDiff);
}

function isCustomDailyRecurrence(date: Date, startDate: Date, config: RecurrenceConfig): boolean {
  return isDailyRecurrence(date, startDate, config);
}

function isCustomWeeklyRecurrence(date: Date, startDate: Date, config: RecurrenceConfig): boolean {
  const interval = config.interval || 1;
  const weeksDiff = Math.floor(
    (date.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24 * 7)
  );
  return weeksDiff >= 0 && weeksDiff % interval === 0 && date.getDay() === startDate.getDay();
}

function isCustomMonthlyRecurrence(date: Date, startDate: Date, config: RecurrenceConfig): boolean {
  const interval = config.interval || 1;
  const monthsDiff =
    (date.getFullYear() - startDate.getFullYear()) * 12 +
    (date.getMonth() - startDate.getMonth());
  
  if (monthsDiff < 0 || monthsDiff % interval !== 0) {
    return false;
  }
  
  // 检查日期是否匹配
  const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const targetDay = Math.min(startDate.getDate(), lastDayOfMonth);
  
  return date.getDate() === targetDay;
}

function isCustomYearlyRecurrence(date: Date, startDate: Date, config: RecurrenceConfig): boolean {
  const interval = config.interval || 1;
  const yearsDiff = date.getFullYear() - startDate.getFullYear();
  
  if (yearsDiff < 0 || yearsDiff % interval !== 0) {
    return false;
  }
  
  return (
    date.getMonth() === startDate.getMonth() && date.getDate() === startDate.getDate()
  );
}

function moveToNextEbbinghausDate(
  current: Date,
  _startDate: Date,
  config: RecurrenceConfig
): void {
  if (!config.ebbinghausStartDate || !config.ebbinghausStages) {
    current.setDate(current.getDate() + 1);
    return;
  }
  
  const start = new Date(config.ebbinghausStartDate);
  const daysDiff = Math.floor((current.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  
  // 找到下一个复习阶段
  const nextStage = config.ebbinghausStages.find((stage) => stage > daysDiff);
  
  if (nextStage !== undefined) {
    const targetDate = new Date(start);
    targetDate.setDate(targetDate.getDate() + nextStage);
    current.setTime(targetDate.getTime());
  } else {
    // 没有下一个阶段，移动到很远以后
    current.setFullYear(current.getFullYear() + 10);
  }
}

/**
 * 获取重复类型的显示名称
 */
export function getRecurrenceTypeLabel(type: RecurrenceType, config?: RecurrenceConfig): string {
  switch (type) {
    case 'daily':
      return config?.interval && config.interval > 1
        ? `每${config.interval}天重复`
        : '日重复';
    
    case 'weekday':
      return '工作日重复(周一 ~ 周五)';
    
    case 'weekly':
      if (config?.weekday !== undefined) {
        const weekdays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
        return `周重复(${weekdays[config.weekday]})`;
      }
      return '周重复';
    
    case 'monthly':
      if (config?.dayOfMonth !== undefined) {
        return `月重复(${config.dayOfMonth}日)`;
      }
      return '月重复';
    
    case 'monthly-last-day':
      return '每月最后一天重复';
    
    case 'quarterly':
      if (config?.quarterDay) {
        return `每季度(${config.quarterDay.month + 1}月${config.quarterDay.day}日)`;
      }
      return '每季度';
    
    case 'yearly':
      if (config?.yearDay) {
        return `年重复(${config.yearDay.month + 1}月${config.yearDay.day}日)`;
      }
      return '年重复';
    
    case 'lunar-yearly':
      if (config?.lunarMonth && config?.lunarDay) {
        return `农历年重复(${config.lunarMonth}月${config.lunarDay}日)`;
      }
      return '农历年重复';
    
    case 'legal-workday':
      return '法定工作日重复';
    
    case 'ebbinghaus':
      return '艾宾浩斯记忆曲线';
    
    case 'custom-daily':
      return `自定义日重复(每${config?.interval || 1}天)`;
    
    case 'custom-weekly':
      return `自定义周重复(每${config?.interval || 1}周)`;
    
    case 'custom-monthly':
      return `自定义月重复(每${config?.interval || 1}月)`;
    
    case 'custom-yearly':
      return `自定义年重复(每${config?.interval || 1}年)`;
    
    default:
      return '未知类型';
  }
}
